import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ClipboardList, Wallet, CheckCircle2, Phone, MapPin, ArrowRight } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { KpiCard } from '../components/common/KpiCard';
import { StatusBadge } from '../components/common/StatusBadge';
import { FleetMap } from '../components/map/FleetMap';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useFleet } from '../contexts/FleetContext';
import { formatTmt, orderStatusLabels } from '../utils/format';
import { cn } from '../utils/cn';
export function Dashboard() {
  const {
    drivers,
    orders
  } = useFleet();
  const [selected, setSelected] = useState<string | null>(null);
  const online = drivers.filter(d => d.status !== 'offline').length;
  const activeOrders = orders.filter(o => o.status !== 'done').length;
  const earnings = drivers.reduce((s, d) => s + d.earningsToday, 0);
  const trips = drivers.reduce((s, d) => s + d.tripsToday, 0);
  const selectedDriver = drivers.find(d => d.id === selected) ?? null;
  return <div data-id="element-240" data-mp="26qufd9hfhwks">
      <PageHeader title="Главная" description="Оперативная сводка по автопарку на сегодня" actions={<Button variant="outline" size="sm" data-id="element-242" data-mp="2fppp2vjolosu">
            Обновить данные
          </Button>} data-id="element-241" data-mp="2bnuppwlb9lhp" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-id="element-243" data-mp="1j08ats6muufj">
        <KpiCard title="Водители онлайн" value={`${online} из ${drivers.length}`} hint="за последний час" delta={8} icon={Users} tone="blue" data-id="element-244" data-mp="1nxqm3p84hlqp" />
        <KpiCard title="Активные заказы" value={String(activeOrders)} hint="в работе сейчас" delta={12} icon={ClipboardList} tone="amber" data-id="element-245" data-mp="1ryak0v0istdw" />
        <KpiCard title="Заработок за сегодня" value={formatTmt(earnings)} hint="ко вчерашнему дню" delta={5} icon={Wallet} tone="green" data-id="element-246" data-mp="108ntjmbya8hf" />
        <KpiCard title="Выполненные поездки" value={String(trips)} hint="с начала смены" delta={-3} icon={CheckCircle2} tone="slate" data-id="element-247" data-mp="1497smvg33m67" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3" data-id="element-248" data-mp="27itzcskmoc9s">
        <Card className="order-2 flex flex-col xl:order-1" data-id="element-249" data-mp="22lkezrc3iqst">
          <CardHeader className="pb-3" data-id="element-250" data-mp="2hbhw5nblyg8i">
            <CardTitle className="text-base" data-id="element-251" data-mp="2hagl3iuldtqs">Водители на линии</CardTitle>
          </CardHeader>
          <CardContent className="flex-1 space-y-2 overflow-y-auto p-3 pt-0 xl:max-h-[540px]" data-id="element-252" data-mp="2ccf2ez12vwfu">
            {drivers.map(driver => <button key={driver.id} type="button" onClick={() => setSelected(driver.id)} className={cn('w-full rounded-lg border p-3 text-left transition-colors', selected === driver.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent')} data-id="element-253" data-mp="1jn7rt1ocnge8">
                <div className="flex items-center justify-between gap-2" data-id="element-254" data-mp="3940hgbgqb9p7">
                  <span className="truncate text-sm font-medium text-foreground" data-id="element-255" data-mp="35017kbpyf94t">{driver.name}</span>
                  <StatusBadge status={driver.status} data-id="element-256" data-mp="3cis888um3152" />
                </div>
                <p className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground" data-id="element-257" data-mp="3hk7x6bzwtrsq">
                  <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" data-id="element-258" data-mp="32n7cutk1sz4a" />
                  {driver.location}
                </p>
                <p className="mt-1 text-xs text-muted-foreground" data-id="element-259" data-mp="37n35xxr7j652">
                  {driver.car} · {driver.tripsToday} поездок · {formatTmt(driver.earningsToday)}
                </p>
              </button>)}
          </CardContent>
        </Card>

        <Card className="order-1 overflow-hidden xl:order-2 xl:col-span-2" data-id="element-260" data-mp="3ljqfr149dqse">
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3" data-id="element-261" data-mp="3qjcs78c9j305">
            <CardTitle className="text-base" data-id="element-262" data-mp="i4b826jcrtaf">Карта автопарка · Ашхабад</CardTitle>
            <span className="text-xs text-muted-foreground" data-id="element-263" data-mp="i288auseelsi">Обновлено только что</span>
          </CardHeader>
          <CardContent className="p-3 pt-0" data-id="element-264" data-mp="2vbua6ms9pt88">
            <div className="h-[320px] sm:h-[420px] xl:h-[480px]" data-id="element-265" data-mp="2vclvn4s5iaks">
              <FleetMap drivers={drivers} selectedId={selected} onSelect={setSelected} data-id="element-266" data-mp="2qd0zf24d8gbr" />
            </div>
            {selectedDriver && <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 p-3" data-id="element-267" data-mp="2le6ftxykqnql">
                <div className="min-w-0" data-id="element-268" data-mp="2ggo4k0x33wff">
                  <p className="text-sm font-medium text-foreground" data-id="element-269" data-mp="3e2v611y3d8nk">{selectedDriver.name}</p>
                  <p className="truncate text-xs text-muted-foreground" data-id="element-270" data-mp="392pbne0ft81s">
                    {selectedDriver.car} · {selectedDriver.plate} · {selectedDriver.location}
                  </p>
                </div>
                <div className="ml-auto flex items-center gap-2" data-id="element-271" data-mp="2ze7fxb70lr50">
                  <Button variant="outline" size="sm" data-id="element-272" data-mp="2zbl614wnizjm">
                    <Phone className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-273" data-mp="o79tu13aoqm" />
                    {selectedDriver.phone}
                  </Button>
                  <Button size="sm" onClick={() => setSelected(null)} data-id="element-274" data-mp="45cwg2hh7wf3">
                    Скрыть
                  </Button>
                </div>
              </div>}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-4" data-id="element-275" data-mp="d5rt1xzw4xxw">
        <CardHeader className="flex-row items-center justify-between space-y-0 pb-3" data-id="element-276" data-mp="d4ggb8ogwfun">
          <CardTitle className="text-base" data-id="element-277" data-mp="i5xf8yne5s8l">Последние заказы</CardTitle>
          <Link to="/orders" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-id="element-278" data-mp="n4qv2xq5hvbx">
            Все заказы
            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" data-id="element-279" data-mp="r3zmvx1mrj4u" />
          </Link>
        </CardHeader>
        <CardContent className="p-3 pt-0" data-id="element-280" data-mp="vxasxvymbvvp">
          <ul className="divide-y divide-border" data-id="element-281" data-mp="10w5cx18bnqhe">
            {orders.slice(0, 5).map(order => <li key={order.id} className="flex flex-col gap-1.5 px-2 py-3 transition-colors hover:bg-accent/50 sm:flex-row sm:items-center sm:gap-3" data-id="element-282" data-mp="15v8gsickfqns">
                <span className="text-xs font-medium text-muted-foreground sm:w-20" data-id="element-283" data-mp="l54mabbeyjo4">{order.id}</span>
                <span className="text-sm font-medium text-foreground sm:w-44 sm:truncate" data-id="element-284" data-mp="g730vjqim89h">{order.customer}</span>
                <span className="min-w-0 truncate text-sm text-muted-foreground sm:flex-1" data-id="element-285" data-mp="uwiquxi829o4">{order.pickup}</span>
                <span className="flex items-center gap-3 sm:ml-auto" data-id="element-286" data-mp="q3z4pc5gy67p">
                  <span className="text-sm font-medium text-foreground" data-id="element-287" data-mp="13vm8ry5nkj11">{formatTmt(order.price)}</span>
                  <span className={cn('rounded-full border px-2.5 py-0.5 text-xs font-medium', order.status === 'new' && 'border-primary/30 bg-primary/10 text-primary', order.status === 'assigned' && 'border-amber-500/30 bg-amber-500/10 text-amber-700', order.status === 'done' && 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700')} data-id="element-288" data-mp="yxu9gj2ohumt">
                    {orderStatusLabels[order.status]}
                  </span>
                </span>
              </li>)}
          </ul>
        </CardContent>
      </Card>
    </div>;
}